import { IconSet } from '@postscript/components';
import {
  CONVERSATION_VIEW_ENABLED,
  EMAIL_BLOCK_BUILDER,
  NAV_UPDATES_SHOPPER,
  SSAI,
} from 'components/admin/utils/feature-flags';
import { useShopperBillingStatus } from 'components/billing/modules/plans/hooks/useShopperBillingStatus';
import { BrandCenterRoutes } from 'components/brand/types';
import UnresolvedNotificationsBadge from 'components/responses/UnresolvedNotificationsBadge';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useMemo } from 'react';
import { NavMenuItem, SubMenuItem } from '../NavMenu';

export const useMessagingMenuItems = (): NavMenuItem[] => {
  const shopperStatusContext = useShopperStatus();
  const { hasLabsFlag } = usePSLabs();
  const { isEligible: isEligibleForShopper } = useShopperBillingStatus();

  const shouldShowConversationsView =
    hasLabsFlag(SSAI) || hasLabsFlag(CONVERSATION_VIEW_ENABLED);

  const hasNavUpdatesShopper = hasLabsFlag(NAV_UPDATES_SHOPPER);

  const shouldShowEmailTemplates = hasLabsFlag(EMAIL_BLOCK_BUILDER);

  const shouldShowShopperUnderMessaging =
    hasNavUpdatesShopper &&
    (shopperStatusContext.hasShopperFeatureEnabled || isEligibleForShopper);

  return useMemo(
    () => [
      {
        id: 'messaging',
        label: 'Messaging',
        icon: IconSet.Message,
        isLoading: false,
        ariaLabelledBy: 'nav-messaging-title',
        notification: UnresolvedNotificationsBadge(true),
        subMenu: [
          {
            link: hasNavUpdatesShopper ? '/flows/campaigns' : '/campaigns',
            label: 'Campaigns',
            id: 'messaging-campaigns',
          },
          {
            link: hasNavUpdatesShopper ? '/flows/automations' : '/automations',
            label: 'Automations',
            id: 'messaging-automations',
          },
          ...((shouldShowShopperUnderMessaging
            ? [
                {
                  link: BrandCenterRoutes.SHOPPER,
                  label: 'Shopper',
                  id: 'messaging-shopper',
                },
              ]
            : []) as SubMenuItem[]),
          { link: '/segments', label: 'Segments', id: 'messaging-segments' },
          ...(shouldShowEmailTemplates
            ? [
                {
                  link: '/email-templates',
                  label: 'Email Templates',
                  id: 'messaging-email-templates',
                },
              ]
            : []),
          ...(shouldShowConversationsView
            ? []
            : [
                {
                  link: '/responses',
                  label: 'Responses',
                  id: 'messaging-responses',
                  notification: UnresolvedNotificationsBadge(false),
                },
              ]),
          ...(shouldShowConversationsView
            ? [
                {
                  link: `/conversations${
                    shopperStatusContext.isLive ? '' : '?resolved=false'
                  }`,
                  label: 'Conversations',
                  id: 'messaging-conversations',
                  notification: UnresolvedNotificationsBadge(false),
                },
              ]
            : []),
        ],
      },
    ],
    [
      shopperStatusContext,
      shouldShowConversationsView,
      shouldShowShopperUnderMessaging,
      hasNavUpdatesShopper,
      shouldShowEmailTemplates,
    ],
  );
};
